import { Input } from "@/components/ui";

export const DEFAULT_CREATOR_WELCOME_SUBJECT = "You're in! Welcome to the Prima Club Creators Program.";
export const DEFAULT_CREATOR_WELCOME_MESSAGE = "Great news - you've been approved for The Prima Club Creators program!";

type AffiliateType = "creator" | "ambassador";

interface ApprovalEmailEditorProps {
  to: string;
  firstName: string;
  affiliateType: AffiliateType;
  logoUrl: string | null;
  commissionPct: number;
  customerDiscountPct: number;
  codeDraft: string;
  subject: string;
  onSubjectChange: (subject: string) => void;
  message: string;
  onMessageChange: (message: string) => void;
}

const STORE_DISCOUNT_URL = "https://theprimaclub.com/discount";

export function ApprovalEmailEditor({
  to,
  firstName,
  affiliateType,
  logoUrl,
  commissionPct,
  customerDiscountPct,
  codeDraft,
  subject,
  onSubjectChange,
  message,
  onMessageChange,
}: ApprovalEmailEditorProps) {
  const code = codeDraft.trim().toUpperCase();
  const previewCode = code || "[created after approval]";
  const discountLink = code
    ? `${STORE_DISCOUNT_URL}/${encodeURIComponent(code)}`
    : `${STORE_DISCOUNT_URL}/[code]`;
  const isCreator = affiliateType === "creator";

  return (
    <div className="space-y-4">
      <p className="text-sm text-zinc-300">
        {isCreator
          ? "Personalize the subject and message. The code, rates, creator benefits, requirements, and one-tap link are included automatically."
          : "Personalize the subject and message. The code, rates, and one-tap link are included automatically."}
      </p>

      <label className="block space-y-1.5">
        <span className="text-sm font-medium text-zinc-200">Subject</span>
        <Input
          value={subject}
          onChange={(event) => onSubjectChange(event.target.value)}
          maxLength={200}
          placeholder={DEFAULT_CREATOR_WELCOME_SUBJECT}
        />
      </label>

      <label className="block space-y-1.5">
        <span className="text-sm font-medium text-zinc-200">Personal message</span>
        <span className="block text-xs text-zinc-400">
          This replaces the standard opening line. Add a personal welcome or acknowledge the wait.
        </span>
        <textarea
          value={message}
          onChange={(event) => onMessageChange(event.target.value)}
          maxLength={4000}
          rows={4}
          className="w-full resize-y rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-500 focus:border-zinc-500 focus:outline-none focus:ring-2 focus:ring-zinc-600"
        />
      </label>

      <section className="rounded-lg border border-zinc-300 bg-white p-5" aria-label="Welcome email preview">
        <div className="space-y-2 border-b border-zinc-300 pb-3 text-sm">
          <div className="flex flex-wrap gap-x-2">
            <span className="font-semibold text-zinc-800">To</span>
            <span className="break-all font-medium text-black">{to}</span>
          </div>
          <div className="flex flex-wrap gap-x-2">
            <span className="font-semibold text-zinc-800">Subject</span>
            <span className="break-words font-medium text-black">{subject.trim() || "Subject required"}</span>
          </div>
        </div>

        <div className="pt-4 font-sans text-base font-medium leading-7 text-black">
          <p className="mb-4 font-bold">Hi {firstName},</p>
          {message.trim() && <p className="mb-4 whitespace-pre-wrap">{message.trim()}</p>}

          <p className="mb-2 font-bold">Your personal discount code:</p>
          <p className="mb-5">
            <span className="inline-block rounded-md bg-[#efcbd0] px-3 py-2 text-lg font-bold tracking-wide text-black">
              {previewCode}
            </span>
          </p>

          {isCreator ? (
            <>
              <h3 className="mb-2 mt-5 text-lg font-bold text-black">Perks &amp; Benefits</h3>
              <ul className="list-disc space-y-2 pl-6">
                <li>Friends and followers get {customerDiscountPct}% off with your personalized code.</li>
                <li>Earn {commissionPct}% commission on every sale made using your code.</li>
                <li>After every 5 sales using your code, you earn a free Prima piece. You choose any item!</li>
              </ul>

              <h3 className="mb-2 mt-5 text-lg font-bold text-black">Requirements</h3>
              <ul className="list-disc space-y-2 pl-6">
                <li>Creators must apply and be approved to participate. You&apos;re officially approved!</li>
                <li>Share one feed post each month (a Reel or carousel).</li>
                <li>Keep your content authentic and on-brand, and clearly tag @theprimaclub.</li>
              </ul>
            </>
          ) : (
            <p className="mb-4">
              Share it with your community and they get {customerDiscountPct}% off anything in the shop. You earn {commissionPct}% commission on every sale made with your code, tracked automatically.
            </p>
          )}

          <p className="mb-5 mt-5 break-words">
            One-tap link that applies your code: <span className="font-semibold underline">{discountLink}</span>
          </p>
          <p className="font-bold">Thanks for dancing with us!<br />The Prima Club</p>
        </div>

        {logoUrl && (
          <div className="mt-5 rounded-lg bg-[#1d1417] px-4 py-3 text-center" aria-label="Light-pink Prima Club email footer">
            <img src={logoUrl} alt="The Prima Club" className="mx-auto h-auto w-40 max-w-full" />
          </div>
        )}
      </section>
      <p className="text-xs text-zinc-400">
        The final code and link are filled in after approval. {code ? "The preview uses your custom code." : "The preview shows placeholders for the code and link."}
      </p>
    </div>
  );
}
